import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, MoreHorizontal, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface Artist {
  _id: string;
  name: string;
  profileType?: "artist" | "listener";
  description?: string;
  location?: string;
  createdAt?: string;
  updatedAt?: string;
}

export function Artists() {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const navigate = useNavigate();

  useEffect(() => {
    setBreadcrumbs([
      { label: "Admin", path: "/admin" },
      { label: "Artists", path: "/admin/artists" },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  useEffect(() => {
    async function fetchArtists() {
      try {
        const response = await fetch("/api/artists");
        if (response.ok) {
          const data = await response.json();
          setArtists(data.artists || []);
        } else {
          console.error("Failed to fetch artists");
        }
      } catch (error) {
        console.error("Failed to fetch artists:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchArtists();
  }, []);

  const filteredArtists = useMemo(() => {
    if (!searchQuery.trim()) {
      return artists;
    }

    const query = searchQuery.toLowerCase();
    return artists.filter((artist) =>
      artist.name.toLowerCase().includes(query) ||
      artist.description?.toLowerCase().includes(query) ||
      artist.location?.toLowerCase().includes(query)
    );
  }, [artists, searchQuery]);

  const handleArtistClick = async (artistId: string) => {
    try {
      const response = await fetch("/api/admin/switch-artist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ artistId }),
      });

      if (response.ok) {
        window.location.href = "/";
      } else {
        console.error("Failed to switch artist");
      }
    } catch (error) {
      console.error("Failed to switch artist:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold">Artists</h1>
          <Button asChild size="icon" variant="outline">
            <Link to="/admin/artists/create" aria-label="Create artist">
              <Plus className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <Input
          type="text"
          placeholder="Search artists..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredArtists.length > 0 ? (
            filteredArtists.map((artist) => (
              <TableRow 
                key={artist._id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleArtistClick(artist._id)}
              >
                <TableCell className="font-medium">{artist.name}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => navigate(`/admin/artists/${artist._id}/overview`)}
                      >
                        Edit
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2} className="h-24 text-center">
                {searchQuery ? "No artists found matching your search." : "No artists found."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

